import React, { useState, useEffect, useRef, useMemo } from "react";
import Select from "react-select";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Draggable from "react-draggable";

const AddAdditionModal = ({ onClose, onAdd, record }) => {
  const [name, setName] = useState("");
  const [category, setCategory] = useState(null);
  const [incomeType, setIncomeType] = useState(null);
  const [affectsNetSalary, setAffectsNetSalary] = useState(true);
  const [taxable, setTaxable] = useState(false);
  const [gratuityApplicable, setGratuityApplicable] = useState(false);
  const [bonusApplicable, setBonusApplicable] = useState(false);
  const [calculationType, setCalculationType] = useState(null);
  const [unitAmount, setUnitAmount] = useState("");
  const [description, setDescription] = useState("");
  const [formula, setFormula] = useState("");
  const [frequency, setFrequency] = useState(null);
  const [loading, setLoading] = useState(false);

  // ✅ Correct consistent state names
  const [salaryHeads, setSalaryHeads] = useState([]);
  const [selectedHead, setSelectedHead] = useState(null);
  const [showFormulaBuilder, setShowFormulaBuilder] = useState(false);
  const [builderFormula, setBuilderFormula] = useState("");

  const submitButtonRef = useRef(null);
  const isFetching = useRef(false);

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");

  const capitalize = (str = "") =>
    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

  // ✅ Fetch salary heads
  const fetchSalaryHeads = async () => {
    if (isFetching.current) return;
    isFetching.current = true;

    try {
      if (!apiUrl || !token) {
        console.warn("❌ Missing API URL or token");
        return;
      }

      console.log("🌐 Fetching:", `${apiUrl}/payroll/salary-heads`);
      const response = await axios.get(`${apiUrl}/payroll/salary-heads`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("✅ Full API Response:", response);

      const mapped = (response?.data || []).map((h) => ({
        value: h.id,
        label: h.name?.trim() || "Unnamed Head",
        ...h,
      }));

      console.log("✅ Salary Heads:", mapped);
      setSalaryHeads(mapped);
    } catch (error) {
      console.error("❌ Fetch error:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to fetch salary heads. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      isFetching.current = false;
    }
  };

  useEffect(() => {
    fetchSalaryHeads();
  }, []);

const filteredHeads = useMemo(
  () => salaryHeads.filter((head) => head.value !== record?.id),
  [salaryHeads, record]
);

  // ✅ Formula Builder: Insert Head
  const handleInsertHead = () => {
    if (selectedHead) {
      const insertText = `${selectedHead.label}_<${selectedHead.value}>`;
      setBuilderFormula((prev) =>
        prev ? prev + " + " + insertText : insertText
      );
      setSelectedHead(null);
    }
  };

  const handleInsertOperator = (op) => {
    setBuilderFormula((prev) => prev + op);
  };

  const handleApplyFormula = () => {
    setFormula(builderFormula);
    setShowFormulaBuilder(false);
  };

  const handleClearBuilder = () => setBuilderFormula("");
  const openFormulaBuilder = () => {
    setBuilderFormula(formula);
    setShowFormulaBuilder(true);
  };

  const resetForm = () => {
    setName("");
    setCategory(null);
    setIncomeType(null);
    setAffectsNetSalary(true);
    setTaxable(false);
    setGratuityApplicable(false);
    setBonusApplicable(false);
    setCalculationType(null);
    setUnitAmount("");
    setDescription("");
    setFormula("");
    setFrequency(null);
  };

  const categoryOptions = [
    { value: "Earnings", label: "Earnings" },
    { value: "Deductions", label: "Deductions" },
    { value: "Overtime", label: "Overtime" },
  ];

  const incomeTypeOptions = [
    { value: "fixed", label: "Fixed" },
    { value: "variable", label: "Variable" },
  ];

  const calculationTypeOptions = [
    { value: "as_computed_value", label: "As Computed Value" },
    { value: "user_defined_value", label: "User Defined Value" },
    { value: "flat_rate", label: "Flat Rate" },
    { value: "on_attendance", label: "On Attendance" },
    { value: "on_production", label: "On Production" },
  ];

  const frequencyOptions = [
    { value: "monthly", label: "Monthly" },
    { value: "quarterly", label: "Quarterly" },
    { value: "annually", label: "Annually" },
  ];

  const yesNoOptions = [
    { value: true, label: "Yes" },
    { value: false, label: "No" },
  ];

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? "#ff9b44" : "#fff",
      color: state.isFocused ? "#fff" : "#000",
      "&:hover": {
        backgroundColor: "#ff9b44",
      },
    }),
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // (Validation omitted for brevity)
  };

  return (
    <>
      {/* Modal */}
      <div
        id="add_salary_head"
        className="modal custom-modal fade custom-modal-width"
        role="dialog"
      >
        <div
          className="modal-dialog modal-lg"
          style={{
            margin: 0,
            position: "fixed",
            right: 0,
            height: "100vh",
            width: "80%",
          }}
          role="document"
        >
          <div className="modal-content h-100">
            <form onSubmit={handleSubmit} className="d-flex flex-column h-100">
              <div className="modal-header">
                <h5 className="modal-title">
                  {record ? "Edit Salary Head" : "Add Salary Head"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={onClose}
                ></button>
              </div>

              <div
                className="modal-body"
                style={{ maxHeight: "calc(100vh - 90px)", overflowY: "auto" }}
              >
                {/* === FORM FIELDS === */}
                <div className="row mb-3 border-bottom pb-3">
                  <div className="col-md-6">
                    <label className="col-form-label">
                      Salary Head Name <span className="text-danger">*</span>
                    </label>
                    <input
                      className="form-control"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter salary head name"
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="col-form-label">
                      Income Type <span className="text-danger">*</span>
                    </label>
                    <Select
                      placeholder="Select Income Type"
                      options={incomeTypeOptions}
                      value={incomeType}
                      onChange={setIncomeType}
                      styles={customStyles}
                      required
                    />
                  </div>
                </div>

                <div className="row mb-3 border-bottom pb-3">
                  <div className="col-md-6">
                    <label className="col-form-label">
                      Category <span className="text-danger">*</span>
                    </label>
                    <Select
                      placeholder="Select Category"
                      options={categoryOptions}
                      value={category}
                      onChange={setCategory}
                      styles={customStyles}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="col-form-label">
                      Calculation Type <span className="text-danger">*</span>
                    </label>
                    <Select
                      placeholder="Select Calculation Type"
                      options={calculationTypeOptions}
                      value={calculationType}
                      onChange={setCalculationType}
                      styles={customStyles}
                      required
                    />
                  </div>
                </div>

                {calculationType?.value === "flat_rate" && (
                  <div className="row mb-3 border-bottom pb-3">
                    <div className="col-md-6">
                      <label className="col-form-label">Unit Amount</label>
                      <div className="input-group">
                        <span className="input-group-text">$</span>
                        <input
                          type="text"
                          className="form-control"
                          value={unitAmount}
                          onChange={(e) => setUnitAmount(e.target.value)}
                          placeholder="Enter unit amount"
                        />
                        <span className="input-group-text">.00</span>
                      </div>
                    </div>
                  </div>
                )}

                {calculationType?.value === "as_computed_value" && (
                  <div className="row mb-3 border-bottom pb-3">
                    <div className="col-md-6">
                      <label className="col-form-label">Formula</label>
                      <div className="d-flex align-items-center">
                        <input
                          className="form-control me-2"
                          type="text"
                          value={formula}
                          readOnly
                          placeholder="Formula will appear here"
                        />
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={openFormulaBuilder}
                        >
                          Build Formula
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="row mb-3 border-bottom pb-3">
                  <div className="col-md-6">
                    <label className="col-form-label">
                      Affects Net Salary
                    </label>
                    <Select
                      options={yesNoOptions}
                      value={yesNoOptions.find(
                        (o) => o.value === affectsNetSalary
                      )}
                      onChange={(o) => setAffectsNetSalary(o.value)}
                      styles={customStyles}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="col-form-label">Taxable</label>
                    <Select
                      options={yesNoOptions}
                      value={yesNoOptions.find((o) => o.value === taxable)}
                      onChange={(o) => setTaxable(o.value)}
                      styles={customStyles}
                    />
                  </div>
                </div>

                <div className="row mb-3 border-bottom pb-3">
                  <div className="col-md-6">
                    <label className="col-form-label">
                      Gratuity Applicable
                    </label>
                    <Select
                      options={yesNoOptions}
                      value={yesNoOptions.find(
                        (o) => o.value === gratuityApplicable
                      )}
                      onChange={(o) => setGratuityApplicable(o.value)}
                      styles={customStyles}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="col-form-label">Bonus Applicable</label>
                    <Select
                      options={yesNoOptions}
                      value={yesNoOptions.find((o) => o.value === bonusApplicable)}
                      onChange={(o) => setBonusApplicable(o.value)}
                      styles={customStyles}
                    />
                  </div>
                </div>

                <div className="row mb-3 border-bottom pb-3">
                  <div className="col-md-6">
                    <label className="col-form-label">Frequency</label>
                    <Select
                      placeholder="Select Frequency"
                      options={frequencyOptions}
                      value={frequency}
                      onChange={setFrequency}
                      styles={customStyles}
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-12">
                    <label className="col-form-label">Description</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Enter description"
                    />
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                  data-bs-dismiss="modal"
                >
                  Close
                </button>
                <button
                  className="btn btn-primary"
                  type="submit"
                  disabled={loading}
                >
                  {loading
                    ? "Saving..."
                    : record
                    ? "Save Changes"
                    : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* ✅ Draggable Formula Builder */}
      {showFormulaBuilder && (
        <Draggable handle=".modal-header">
          <div
            style={{
              position: "fixed",
              top: "20%",
              left: "30%",
              width: "40%",
              minWidth: "400px",
              zIndex: 1055,
              backgroundColor: "#fff",
              borderRadius: "10px",
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.25)",
              border: "1px solid #ddd",
            }}
            className="draggable-formula-builder"
          >
            <div
              className="modal-header"
              style={{
                backgroundColor: "#f8f9fa",
                cursor: "move",
                borderBottom: "1px solid #dee2e6",
              }}
            >
              <h5 className="modal-title mb-0 fw-semibold">
                🧮 Formula Builder
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowFormulaBuilder(false)}
              ></button>
            </div>

            <div
              className="modal-body p-3"
              style={{ maxHeight: "65vh", overflowY: "auto" }}
            >
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  Preview Formula
                </label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={builderFormula}
                  onChange={(e) => setBuilderFormula(e.target.value)}
                  placeholder="Build your formula here (e.g., Basic_<123> * 0.12)"
                />
              </div>

              {/* ✅ Salary Head Selector */}
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  Insert Salary Head
                </label>
                <div className="d-flex align-items-center">
<Select
  options={salaryHeads}
  value={selectedHead}
  onChange={(option) => setSelectedHead(option)}
  placeholder="Select Salary Head"
  className="flex-grow-1 me-2"
  menuPlacement="auto"
  menuPosition="fixed"
  styles={{
    menu: (provided) => ({ ...provided, zIndex: 9999 }),
    control: (provided) => ({ ...provided, overflow: "visible" }),
  }}
/>


                  <button
                    type="button"
                    className="btn btn-outline-primary ms-2"
                    onClick={handleInsertHead}
                    disabled={!selectedHead}
                  >
                    Insert
                  </button>
                </div>
              </div>

              {/* Operators */}
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  Insert Operator / Function
                </label>
                <div className="d-flex flex-wrap gap-2">
                  {[
                    "+",
                    "-",
                    "*",
                    "/",
                    "%",
                    "(",
                    ")",
                    "Ceil(",
                    "Floor(",
                    "Min(",
                    "Max(",
                    "0.12",
                    "12",
                  ].map((op) => (
                    <button
                      key={op}
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => handleInsertOperator(op)}
                    >
                      {op}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="modal-footer bg-light py-2 d-flex justify-content-between">
              <div>
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm me-2"
                  onClick={handleClearBuilder}
                >
                  Clear
                </button>
              </div>
              <div>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm me-2"
                  onClick={() => setShowFormulaBuilder(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={handleApplyFormula}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </Draggable>
      )}
    </>
  );
};

export default AddAdditionModal;
